import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface IncomeCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}
export function IncomeCalculator({
  isOpen,
  onClose
}: IncomeCalculatorProps) {
  const [contacts, setContacts] = useState(10);
  const [closeRate, setCloseRate] = useState(30);
  const [price, setPrice] = useState(350);
  const [recurring, setRecurring] = useState(false);
  const [bump, setBump] = useState(false);
  // Calculate results
  const closed = Math.round(contacts * (closeRate / 100));
  const sitesIncome = closed * price;
  const bumpIncome = bump ? closed * 17 : 0;
  const weeklyIncome = sitesIncome + bumpIncome;
  const monthlyIncome = weeklyIncome * 4;
  const recurringMonth = recurring ? closed * 4 * 99 : 0;
  const yearlyIncome = monthlyIncome * 12 + recurringMonth * 12;
  const fmt = (n: number) => {
    return '$' + Math.round(n).toLocaleString('en-US');
  };
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-128">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" data-id="element-129" />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] overflow-y-auto" data-id="element-130">
            <div className="min-h-full flex items-center justify-center p-4" data-id="element-131">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.3
          }} className="relative w-full max-w-4xl bg-navy border border-gold/30 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-132">
                {/* Close button */}
                <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-navy-dark/50 hover:bg-navy-dark border border-gold/20 hover:border-gold/40 transition-colors" aria-label="Cerrar calculadora" data-id="element-133">
                  <X className="h-5 w-5 text-gray-300" data-id="element-134" />
                </button>

                {/* Content */}
                <div className="p-6 md:p-8 max-h-[85vh] overflow-y-auto" data-id="element-135">
                  {/* Header */}
                  <div className="text-center mb-8" data-id="element-136">
                    <h3 className="font-heading font-black text-3xl md:text-4xl mb-2" data-id="element-137">
                      ¿Cuánto Puedes Ganar{' '}
                      <span className="text-gold" data-id="element-138">Esta Semana?</span>
                    </h3>
                    <p className="font-body text-sm text-gray-400" data-id="element-139">
                      Mueve los controles y calcula tu ingreso real con el
                      sistema AI Web Closer
                    </p>
                  </div>

                  {/* Calculator Card */}
                  <div className="bg-navy-dark border border-gold/20 rounded-xl p-6 md:p-8 mb-6" data-id="element-140">
                    <div className="font-heading font-bold text-xs tracking-widest text-gold uppercase mb-6" data-id="element-141">
                      Calculadora de Ingresos
                    </div>

                    {/* Slider 1: Contacts */}
                    <div className="mb-6" data-id="element-142">
                      <div className="flex justify-between items-center mb-2" data-id="element-143">
                        <span className="font-body text-sm text-gray-300" data-id="element-144">
                          Negocios que contactas por semana
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[60px] text-center" data-id="element-145">
                          {contacts}
                        </div>
                      </div>
                      <input type="range" min="1" max="50" value={contacts} onChange={e => setContacts(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-146" />
                    </div>

                    {/* Slider 2: Close Rate */}
                    <div className="mb-6" data-id="element-147">
                      <div className="flex justify-between items-center mb-2" data-id="element-148">
                        <span className="font-body text-sm text-gray-300" data-id="element-149">
                          Tasa de cierre (% que dice SÍ)
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[60px] text-center" data-id="element-150">
                          {closeRate}
                          <span className="text-xs" data-id="element-151">%</span>
                        </div>
                      </div>
                      <input type="range" min="5" max="60" value={closeRate} onChange={e => setCloseRate(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-152" />
                    </div>

                    {/* Slider 3: Price */}
                    <div className="mb-8" data-id="element-153">
                      <div className="flex justify-between items-center mb-2" data-id="element-154">
                        <span className="font-body text-sm text-gray-300" data-id="element-155">
                          Precio por sitio web
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[70px] text-center" data-id="element-156">
                          ${price}
                        </div>
                      </div>
                      <input type="range" min="150" max="1500" step="50" value={price} onChange={e => setPrice(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-157" />
                    </div>

                    {/* Divider */}
                    <div className="relative mb-6" data-id="element-158">
                      <div className="absolute inset-0 flex items-center" data-id="element-159">
                        <div className="w-full border-t border-gold/20" data-id="element-160"></div>
                      </div>
                    </div>

                    {/* Toggle: Recurring */}
                    <div className="flex items-center gap-3 mb-4 p-3 bg-navy-light/50 rounded-lg border border-gold/10" data-id="element-161">
                      <span className="font-body text-sm text-gray-300 flex-1" data-id="element-162">
                        Añadir paquete de mantenimiento mensual ($99/cliente)
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-163">
                        <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} className="sr-only peer" data-id="element-164" />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold" data-id="element-165"></div>
                      </label>
                      <div className="font-heading font-bold text-xs text-navy bg-gold rounded px-2 py-1" data-id="element-166">
                        +$99/mes
                      </div>
                    </div>

                    {/* Toggle: Bump */}
                    <div className="flex items-center gap-3 mb-8 p-3 bg-navy-light/50 rounded-lg border border-gold/10" data-id="element-167">
                      <span className="font-body text-sm text-gray-300 flex-1" data-id="element-168">
                        Incluir upsell Kit del Primer Cliente ($17)
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-169">
                        <input type="checkbox" checked={bump} onChange={e => setBump(e.target.checked)} className="sr-only peer" data-id="element-170" />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold" data-id="element-171"></div>
                      </label>
                      <div className="font-heading font-bold text-xs text-navy bg-gold rounded px-2 py-1" data-id="element-172">
                        +$17
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div className="pt-2" data-id="element-173">
                      <div className="font-heading text-xs font-bold tracking-widest text-gray-400 uppercase mb-4" data-id="element-174">
                        Desglose Semanal
                      </div>
                      <div className="space-y-2" data-id="element-175">
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-176">
                          <span className="font-body text-sm text-gray-400" data-id="element-177">
                            Negocios contactados
                          </span>
                          <span className="font-heading font-bold text-white" data-id="element-178">
                            {contacts}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-179">
                          <span className="font-body text-sm text-gray-400" data-id="element-180">
                            Clientes cerrados
                          </span>
                          <span className="font-heading font-bold text-white" data-id="element-181">
                            {closed}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-182">
                          <span className="font-body text-sm text-gray-400" data-id="element-183">
                            Ingreso por sitios web
                          </span>
                          <span className="font-heading font-bold text-gold" data-id="element-184">
                            {fmt(sitesIncome)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-185">
                          <span className="font-body text-sm text-gray-400" data-id="element-186">
                            Ingreso recurrente (mes 1)
                          </span>
                          <span className="font-heading font-bold text-gold" data-id="element-187">
                            {recurring ? `${fmt(closed * 99)}/sem` : '$0'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-t border-gold/20 mt-2" data-id="element-188">
                          <span className="font-heading font-bold text-white" data-id="element-189">
                            Total semana 1
                          </span>
                          <span className="font-heading font-black text-xl text-gold" data-id="element-190">
                            {fmt(weeklyIncome)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Reference */}
                  <div className="bg-navy-dark border border-gold/20 rounded-xl p-6 md:p-8 mb-6" data-id="element-191">
                    <div className="font-heading font-bold text-xs tracking-widest text-gold uppercase mb-6" data-id="element-192">
                      💰 Precios de Referencia por Nicho
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-193">
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-194">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-195">
                          🍕 Restaurantes
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-196">
                          $300–$500
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-197">
                          Alta urgencia
                        </div>
                      </div>
                      <div className="bg-gold/10 border-2 border-gold rounded-lg p-4 text-center relative" data-id="element-198">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-199">
                          ⭐ RECOMENDADO
                        </div>
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-200">
                          🦷 Dentistas
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-201">
                          $500–$900
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-202">
                          Mayor ticket
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-203">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-204">
                          🔧 Talleres
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-205">
                          $250–$450
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-206">
                          Fácil de cerrar
                        </div>
                      </div>
                      <div className="bg-gold/10 border-2 border-gold rounded-lg p-4 text-center relative" data-id="element-207">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-208">
                          ⭐ RECOMENDADO
                        </div>
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-209">
                          ⚖️ Abogados
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-210">
                          $700–$1,500
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-211">
                          Máximo ticket
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-212">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-213">
                          ✂️ Peluquerías
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-214">
                          $200–$350
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-215">
                          Volumen alto
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-216">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-217">
                          🏋️ Gimnasios
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-218">
                          $350–$600
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-219">
                          Buen margen
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-gradient-to-br from-gold/15 to-gold/5 border border-gold/40 rounded-xl p-6 md:p-8 text-center" data-id="element-220">
                    <h4 className="font-heading font-black text-2xl md:text-3xl mb-3" data-id="element-221">
                      ¿Listo para Ganar Tu Primer{' '}
                      <span className="text-gold" data-id="element-222">$1,000 Esta Semana?</span>
                    </h4>
                    <p className="font-body text-sm text-gray-300 mb-6 max-w-2xl mx-auto" data-id="element-223">
                      Accede al sistema completo AI Web Closer por solo $37.
                      Incluye los 4 módulos, los 2 cerebros de IA personalizados
                      y el guion de cierre. Garantía de 30 días.
                    </p>
                    <motion.a href="/checkout" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="inline-block bg-gradient-to-r from-gold to-gold-dark text-navy font-heading font-black text-lg px-10 py-4 rounded-full shadow-2xl hover:shadow-gold/50 transition-all" data-id="element-224">
                      🚀 QUIERO ACCESO POR $37 AHORA →
                    </motion.a>
                    <div className="font-body text-xs text-gray-500 mt-4" data-id="element-225">
                      ⚡ Acceso inmediato · 🔒 Garantía 30 días · 💳 Pago único
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </AnimatePresence>;
}